import { useState } from "react";
import { Check, Loader2, X } from "lucide-react";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import RichTextEditor from "../ui/RichTextEditor";
import { updateFieldContent } from "../../services/consultationform";

import { FormField } from "../../types/forms";

interface TextBlockSectionProps {
  userId: string;
  templateId: string;
  fields?: FormField[];
  isEditable?: boolean;
  /** Section title – editable in preview when isEditable (same as heading) */
  sectionTitle?: string | null;
  /** Update section title in template state when editing in preview */
  onTitleChange?: (value: string) => void;
  /** First field id for title API (same as heading) */
  firstFieldId?: string | number;
  /** Save title via API when user clicks tick */
  onSaveTitle?: (fieldId: string | number, title: string) => void | Promise<void>;
}

export default function TextBlockSection({
  userId,
  templateId,
  fields,
  isEditable = false,
  sectionTitle,
  onTitleChange,
  firstFieldId,
  onSaveTitle,
}: TextBlockSectionProps) {
  const storageKey = `${userId}_${templateId}_textblock`;
  const [text, setText] = useLocalStorage<string>(storageKey, "");

  const [isEditing, setIsEditing] = useState(false);
  const [tempContent, setTempContent] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingTitle, setIsSavingTitle] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);

  const field = fields && fields.length > 0 ? fields[0] : null;
  const displayContent = field ? (field.options?.content || field.label) : text;
  const label = sectionTitle?.trim() || "Text Block";

  const handleTitleFocus = () => setIsEditingTitle(true);
  const handleTitleBlur = () => setTimeout(() => setIsEditingTitle(false), 150);

  const handleStartEdit = () => {
    if (!field || !isEditable) return;
    setTempContent(displayContent);
    setIsEditing(true);
  };

  const handleSaveEdit = async () => {
    if (!field) return;
    setIsSaving(true);
    try {
      await updateFieldContent(field.id, tempContent);
      setIsEditing(false);
      if (field.options) field.options.content = tempContent;
      else field.label = tempContent;
    } catch (err) {
      console.error("Failed to save text block:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveTitle = async () => {
    if (!firstFieldId || !onSaveTitle) return;
    const titleToSave = label || "Text Block";
    setIsSavingTitle(true);
    try {
      await Promise.resolve(onSaveTitle(firstFieldId, titleToSave));
      setIsEditingTitle(false);
    } finally {
      setIsSavingTitle(false);
    }
  };

  const handleCloseTitle = () => setIsEditingTitle(false);

  const canEditTitle = isEditable && firstFieldId && onSaveTitle && onTitleChange;
  const showTitleSaveClose = canEditTitle && isEditingTitle;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        {canEditTitle ? (
          <>
            <input
              value={label}
              onChange={(e) => onTitleChange?.(e.target.value)}
              onFocus={handleTitleFocus}
              onBlur={handleTitleBlur}
              placeholder="Text Block"
              className="flex-1 text-xl font-semibold text-gray-800 border-0 border-b border-gray-200 rounded-none px-0 bg-transparent focus-visible:ring-0 focus-visible:border-[#885ABB] outline-none"
            />
            {showTitleSaveClose && (
              <>
                <button
                  type="button"
                  onClick={handleSaveTitle}
                  disabled={isSavingTitle}
                  className="p-1 text-green-500 hover:text-green-600 disabled:opacity-50 shrink-0"
                  title="Save title"
                >
                  {isSavingTitle ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                </button>
                <button type="button" onClick={handleCloseTitle} className="p-1 text-gray-400 hover:text-gray-600 shrink-0" title="Close" aria-label="Close">
                  <X size={16} />
                </button>
              </>
            )}
          </>
        ) : (
          <h2 className="text-xl font-semibold text-gray-800">{label}</h2>
        )}
      </div>

      {isEditing ? (
        <div className="space-y-2">
          <RichTextEditor
            value={tempContent}
            onChange={setTempContent}
            rows={6}
          />
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setIsEditing(false)}
              className="p-1 text-gray-400 hover:text-gray-600"
            >
              <X size={16} />
            </button>
            <button
              disabled={isSaving}
              onClick={handleSaveEdit}
              className="p-1 text-green-500 hover:text-green-600 disabled:opacity-50"
            >
              {isSaving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
            </button>
          </div>
        </div>
      ) : (
        <div
          onClick={handleStartEdit}
          className={`prose prose-sm max-w-none text-gray-600 ${isEditable && field ? "cursor-pointer hover:bg-gray-50 p-2 -m-2 rounded transition-colors border border-transparent hover:border-gray-200" : ""}`}
        >
          {field ? (
            <div dangerouslySetInnerHTML={{ __html: displayContent }} />
          ) : (
            // Local storage legacy mode - just edit in place? 
            // Or simple textarea as before? User asked for text editor.
            // Let's use RichTextEditor for local mode too if they want.
            // But for now, keeping legacy behavior simple or upgrading it?
            // "same as privacy/disclaimer" implies click-to-edit.
            // Let's keep consistent.
            <RichTextEditor
              value={text}
              onChange={setText}
              placeholder="Enter text content here..."
            />
          )}
        </div>
      )}
    </div>
  );
}
